/**
 * Which incoming drum feeds which execution — as a COLOUR.
 *
 * The wizard's phases are fed by several incoming drums at once (a rope takes
 * one of every colour, an isolation run can be spliced from two reels, a
 * flat-cable sheath eats one drum per core), and the only record of which drum
 * went where was a count: "From 2 conductors", "From Drum #1793267613006.837".
 * A count cannot be checked against anything, and an id that long is not a
 * number a person reads — so the planner had no way to see, at a glance, that
 * the run they just added is drawing on the drum they meant.
 *
 * This gives every incoming drum an accent colour and a short number, and hands
 * the same pair to the execution cards that consume it. The colour is the thing
 * the eye follows; the number is what the planner says out loud.
 *
 * ── the one rule that makes it work ─────────────────────────────────────────
 *
 * The accent is keyed by the drum's POSITION in `incomingExecutions(product,
 * phase)` — the same canonical list the cards, `toggleSelect` and the
 * "+ Add Execution" gate all read (helpers/incomingDrums.js). Position, not id,
 * because the label on screen is "Drum #3" and the colour has to agree with the
 * number next to it. Both sides deriving from the same list is what guarantees
 * they agree; a second list, or an index taken from `phase.execution`, would
 * drift the moment a drum is filtered out of one and not the other.
 */

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Ten hues, in the order they are handed out.
 *
 * Explicit hex rather than Bootstrap utility classes, for two reasons:
 *
 *  - Phoenix's `-100` tints INVERT between light and dark, so a class that
 *    reads as a pale blue stripe on one theme is a near-black one on the other.
 *    These are mid-tone and saturated, which survives both.
 *  - The drum cards already spend Bootstrap's semantic colours on STATE —
 *    green is finished, blue is selected, amber is forked, cyan is a phantom.
 *    An accent drawn from the same palette would be read as a state. This is a
 *    separate visual channel on purpose, and it is drawn as a stripe and a chip
 *    rather than as a card background so it never competes with those.
 *
 * Deliberately avoids the greens: a green stripe on a card whose own rule for
 * green is "fully allocated" is the one collision that would actually mislead.
 */
export const DRUM_ACCENTS = [
  '#6610f2', // indigo
  '#d63384', // pink
  '#fd7e14', // orange
  '#0dcaf0', // cyan
  '#6f42c1', // purple
  '#b02a37', // crimson
  '#0a58ca', // deep blue
  '#997404', // ochre
  '#2c7a7b', // teal
  '#7f5539'  // brown
];

/** The accent for the Nth drum of a phase's incoming list. Wraps past ten. */
export const accentForIndex = index => {
  const i = Math.trunc(num(index));
  if (i < 0) return DRUM_ACCENTS[0];
  return DRUM_ACCENTS[i % DRUM_ACCENTS.length];
};

/**
 * `incomingExecutions(product, phase)` → Map of id → how to draw that drum.
 *
 * `number` is 1-based because that is what the card prints. A phantom keeps its
 * position in the numbering: it is a real input with real metres, and skipping
 * it would renumber every drum after it relative to what the card shows.
 */
export function drumAccentMap(incoming) {
  const map = new Map();
  if (!Array.isArray(incoming)) return map;

  incoming.forEach((exec, index) => {
    const id = String(exec?.id ?? '');
    if (!id || map.has(id)) return;
    map.set(id, {
      id,
      index,
      number: index + 1,
      color: accentForIndex(index),
      label: exec?.isPhantom ? `Phantom #${index + 1}` : `Drum #${index + 1}`,
      exec
    });
  });

  return map;
}

/**
 * Every input of one execution, in the order IT uses them.
 *
 * Reads the three shapes an execution can record its inputs in, most specific
 * first:
 *
 *   `fromUpstreams: [{id, take}]`  the split, with metres per drum
 *   `fromUpstreamIds: [id]`        the drums, without the split
 *   `fromUpstreamId: id`           a single drum (coiling, rewinding, sheaths)
 *
 * `fromUpstreams` order is the allocation order — which drum is emptied first —
 * so it is kept rather than sorted. A launch writes `fromUpstreamIds: {}` on
 * pre-stranding executions, hence `Array.isArray` and not `?.length`: `{}`
 * answers `undefined` to `.length` and passes every truthiness test.
 *
 * A drum not in `accents` yields `{ unknown: true }` rather than being dropped.
 * That happens honestly — an execution launched against a drum that has since
 * been forked away still names it — and silently showing one fewer input than
 * the execution has would hide exactly that.
 */
export function execInputAccents(exec, accents) {
  const map = accents instanceof Map ? accents : new Map();
  const out = [];
  const seen = new Set();

  const push = (rawId, take) => {
    const id = String(rawId ?? '');
    if (!id || id === 'null' || id === 'undefined' || seen.has(id)) return;
    seen.add(id);
    const hit = map.get(id);
    out.push(
      hit
        ? { ...hit, take: take == null ? null : num(take) }
        : {
            id,
            index: -1,
            number: null,
            // Amber, not grey. A drum that is named but not in the list has
            // almost always been FORKED AWAY, and amber is what the wizard
            // already spends on a forked drum — so the colour says what
            // happened instead of saying "no colour". Grey also reads as dead
            // on the dark theme, which is the other half of why it changed.
            color: '#d6a150',
            label: `Drum ${id.slice(-6)}`,
            exec: null,
            unknown: true,
            take: take == null ? null : num(take)
          }
    );
  };

  const takes = Array.isArray(exec?.fromUpstreams) ? exec.fromUpstreams : [];
  for (const u of takes) push(u?.id, u?.take);

  const ids = Array.isArray(exec?.fromUpstreamIds) ? exec.fromUpstreamIds : [];
  for (const id of ids) push(id, null);

  if (exec?.fromUpstreamId != null) push(exec.fromUpstreamId, null);

  return out;
}

/**
 * The left-edge stripe for a card, as a CSS `background` value.
 *
 * One input is a flat colour. Several are hard-stop bands in the execution's
 * own order, so a run spliced from two reels is visibly a two-colour card and
 * the top band is the drum it empties first. `null` when there is nothing to
 * draw, so the caller can fall back to whatever it drew before.
 */
export function accentStripe(accents) {
  const list = (Array.isArray(accents) ? accents : []).filter(Boolean);
  if (!list.length) return null;
  if (list.length === 1) return list[0].color;

  const step = 100 / list.length;
  const stops = list.map(
    (a, i) => `${a.color} ${(i * step).toFixed(2)}% ${((i + 1) * step).toFixed(2)}%`
  );
  return `linear-gradient(to bottom, ${stops.join(', ')})`;
}

/**
 * One line of detail about an incoming drum, for a tooltip.
 *
 * What a planner actually wants to know about a reel they have just tied a run
 * to: what it is, what is on it, and what will be left. `take` is this
 * execution's share, so the same drum under two runs reads differently on each
 * — which is the point.
 */
export function drumAccentTitle(entry, { capacity, used, take } = {}) {
  if (!entry) return '';
  const bits = [entry.label];

  const src = entry.exec || {};
  if (src.color) bits.push(String(src.color));
  if (num(src.wireDiameter) > 0) bits.push(`Ø ${src.wireDiameter} mm`);
  if (src.wireType) bits.push(String(src.wireType));
  if (src.isPhantom) bits.push('from stock');

  const head = bits.join(' · ');
  const lines = [head];

  if (num(capacity) > 0) {
    const left = Math.max(num(capacity) - num(used), 0);
    lines.push(
      `Holds ${num(capacity).toFixed(1)} m · used ${num(used).toFixed(1)} m · ` +
        `${left.toFixed(1)} m left`
    );
  } else {
    // The incoming PANEL knows what is used off a drum; an execution card does
    // not — the consumed map is built per phase, in a different scope. But the
    // drum's own length travels on the entry, and "500 m of a 2 000 m drum" is
    // most of the point without it.
    const own = num(src.plannedLength) || num(src.plannedLengthInput);
    if (own > 0) lines.push(`Drum holds ${own.toFixed(1)} m`);
  }
  if (num(take) > 0) lines.push(`This execution takes ${num(take).toFixed(1)} m`);

  return lines.join('\n');
}
